import { z } from 'zod';

import { PLATFORM_HOSTS } from '@/config';
import { darkenHex, primaryFailsContrast } from '@/lib/color';

export const THEME_PRESET_IDS = ['modern_luxe', 'commercial_fleet', 'minimal_warm'] as const;
export const THEME_SECTION_TYPES = [
  'announcement_bar',
  'hero_banner',
  'search_filter',
  'featured_fleet',
  'agency_showcase',
  'trust_proof',
  'crm_lead_capture',
  'whatsapp_lead',
  'faq_section',
  'footer',
] as const;

export const FLEET_BADGES = ['Économique', 'SUV', 'Berline', 'Automatique'] as const;
export const AIRPORTS = [
  'Agadir Al Massira',
  'Marrakech Menara',
  'Casablanca Mohammed V',
  'Tangier',
  'Fes',
] as const;

export const THEME_SWATCHES = [
  { id: 'terracotta', label: 'Terracotta', primary: '#C45C26', secondary: '#FFF7ED', tone: 'light' },
  { id: 'majorelle', label: 'Majorelle', primary: '#3B4CC0', secondary: '#E0E7FF', tone: 'light' },
  { id: 'desert_gold', label: 'Desert Gold', primary: '#C4A574', secondary: '#111827', tone: 'dark' },
  { id: 'obsidian', label: 'Obsidian', primary: '#111827', secondary: '#FF8A00', tone: 'dark' },
] as const;

export const RADIUS_OPTIONS = [
  { id: 'rounded-none', label: 'Sharp' },
  { id: 'rounded-md', label: 'Subtle' },
  { id: 'rounded-2xl', label: 'Rounded' },
  { id: 'rounded-full', label: 'Pill' },
] as const;

export const FONT_OPTIONS = [
  { id: 'inter', label: 'Inter' },
  { id: 'montserrat', label: 'Montserrat' },
  { id: 'plus_jakarta', label: 'Plus Jakarta Sans' },
] as const;

export const POLICY_PRESETS = [
  { question: 'What is the minimum driver age?', answer: 'The driver must be at least 21 and have held a licence for one year.' },
  { question: 'How is the deposit paid?', answer: 'The deposit is paid in cash at delivery.' },
  { question: 'Do you accept an international licence?', answer: 'An international driving permit is required for licences issued outside Morocco.' },
] as const;

export const THEME_PREVIEW_MESSAGE = 'fleetava-theme-preview';
export const THEME_PREVIEW_READY = 'fleetava-theme-preview-ready';
export const THEME_PREVIEW_SELECT = 'fleetava-theme-select';

export type ThemePresetId = (typeof THEME_PRESET_IDS)[number];
export type ThemeSectionType = (typeof THEME_SECTION_TYPES)[number];

export const THEME_PRESETS: { id: ThemePresetId; label: string }[] = [
  { id: 'modern_luxe', label: 'Modern Luxe' },
  { id: 'commercial_fleet', label: 'Commercial Fleet' },
  { id: 'minimal_warm', label: 'Minimal Warm' },
];

export const SECTION_LABELS: Record<ThemeSectionType, string> = {
  announcement_bar: 'Announcement',
  hero_banner: 'Hero',
  search_filter: 'Search and airports',
  featured_fleet: 'Featured fleet',
  agency_showcase: 'Pickup map',
  trust_proof: 'Trust',
  crm_lead_capture: 'Reservation',
  whatsapp_lead: 'WhatsApp',
  faq_section: 'FAQ',
  footer: 'Footer',
};

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/);

const tokensSchema = z.object({
  primary: hex,
  secondary: hex,
  tone: z.enum(['dark', 'light', 'slate']),
  font: z.enum(['inter', 'montserrat', 'plus_jakarta']),
  radius: z.enum(['rounded-none', 'rounded-md', 'rounded-2xl', 'rounded-full']),
});

const sectionSchema = z.object({
  id: z.string(),
  type: z.enum(THEME_SECTION_TYPES),
  enabled: z.boolean(),
  settings: z.record(z.string(), z.unknown()),
});

const agencySchema = z.object({
  name: z.string(),
  city: z.string(),
  address: z.string(),
  phone: z.string(),
  whatsapp: z.string(),
  ice: z.string(),
  rc: z.string(),
  patente: z.string(),
  hours: z.array(z.object({ label: z.string(), value: z.string() })).default([]),
});

export const publicThemeSchema = z.object({
  presetId: z.enum(THEME_PRESET_IDS),
  tokens: tokensSchema,
  sections: z.array(sectionSchema),
  customCss: z.string().nullable(),
  logoUrl: z.string(),
  faviconUrl: z.string(),
  agency: agencySchema,
});

const documentSchema = z.object({
  presetId: z.enum(THEME_PRESET_IDS),
  designTokens: tokensSchema,
  sectionsDraft: z.array(sectionSchema),
  sectionsPublished: z.array(sectionSchema).nullable(),
  customCss: z.string().nullable(),
  logoUrl: z.string(),
  faviconUrl: z.string(),
  publishedAt: z.string().nullable(),
  revisions: z.array(z.object({
    id: z.number().int(),
    presetId: z.string(),
    publishedAt: z.string().nullable(),
  })),
});

export type ThemeTokens = z.infer<typeof tokensSchema>;
export type ThemeSection = z.infer<typeof sectionSchema>;
export type ThemeAgency = z.infer<typeof agencySchema>;
export type PublicTheme = z.infer<typeof publicThemeSchema>;
export type ThemeDocument = z.infer<typeof documentSchema>;

const FONT_HREF: Record<ThemeTokens['font'], string> = {
  inter: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap',
  montserrat: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap',
  plus_jakarta: 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap',
};

const FONT_STACK: Record<ThemeTokens['font'], string> = {
  inter: '"Inter", sans-serif',
  montserrat: '"Montserrat", sans-serif',
  plus_jakarta: '"Plus Jakarta Sans", sans-serif',
};

const RADIUS: Record<ThemeTokens['radius'], string> = {
  'rounded-none': '0px',
  'rounded-md': '6px',
  'rounded-2xl': '16px',
  'rounded-full': '999px',
};

export function parseThemeDocument(value: unknown): ThemeDocument {
  const parsed = documentSchema.safeParse(value);
  if (!parsed.success) throw new Error('The theme document could not be read.');
  return parsed.data;
}

export function parsePublicTheme(value: unknown): PublicTheme | null {
  const parsed = publicThemeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function themeLabel(id: string | null): string {
  if (id === null || id === '') return '—';
  return THEME_PRESETS.find((preset) => preset.id === id)?.label
    ?? (id === 'classic' ? 'Classic' : id === 'editorial' ? 'Editorial' : id === 'roadtrip' ? 'Road trip' : id);
}

export function sectionText(settings: ThemeSection['settings'], key: string): string {
  const value = settings[key];
  return typeof value === 'string' ? value : '';
}

export function sectionFlag(settings: ThemeSection['settings'], key: string): boolean {
  return settings[key] === true;
}

export function sectionNumber(settings: ThemeSection['settings'], key: string, fallback: number): number {
  const value = settings[key];
  return typeof value === 'number' ? value : fallback;
}

export function sectionChoices(settings: ThemeSection['settings'], key: string, allowed: readonly string[]): string[] {
  if (!Object.prototype.hasOwnProperty.call(settings, key)) return [...allowed];
  const value = settings[key];
  if (!Array.isArray(value)) return [];
  return allowed.filter((item) => value.includes(item));
}

export function primaryContrastFails(tokens: ThemeTokens): boolean {
  return primaryFailsContrast(tokens.primary, tokens.tone);
}

export function defaultSection(type: ThemeSectionType): ThemeSection {
  switch (type) {
    case 'announcement_bar':
      return section(type, { message: 'Special Ramadan rates. Long-term rentals available.' });
    case 'hero_banner':
      return section(type, {
        layout: 'search',
        tagline: 'Cars ready across Morocco.',
        subheadline: 'No credit card required to reserve.',
        background: 'gradient',
        overlay: 40,
        imageUrl: '',
      });
    case 'search_filter':
      return section(type, { badges: [...FLEET_BADGES], airports: [...AIRPORTS] });
    case 'featured_fleet':
      return section(type, { view: 'grid', limit: 8, badges: [...FLEET_BADGES] });
    case 'agency_showcase':
      return section(type, { heading: 'Pickup points' });
    case 'trust_proof':
      return section(type, { heading: 'Rent with a clear price', notice: 'No credit card required to reserve.' });
    case 'crm_lead_capture':
      return section(type, {
        showWhatsapp: true,
        showForm: true,
        messageTemplate: 'Bonjour, je souhaite réserver une voiture.',
        buttonLabel: 'Send',
      });
    case 'whatsapp_lead':
      return section(type, {
        buttonLabel: 'Réserver sur WhatsApp',
        messageTemplate: 'Bonjour, je souhaite réserver une voiture.',
        phone: '',
        placement: 'sticky',
      });
    case 'faq_section':
      return section(type, { items: [POLICY_PRESETS[0]] });
    case 'footer':
      return section(type, { showLegal: true });
    default: {
      const unexpected: never = type;
      return unexpected;
    }
  }
}

function section(type: ThemeSectionType, settings: ThemeSection['settings']): ThemeSection {
  return { id: type, type, enabled: true, settings };
}

export function faqItems(settings: ThemeSection['settings']): { question: string; answer: string }[] {
  const items = settings.items;
  if (!Array.isArray(items)) return [];
  return items.flatMap((item) => {
    if (typeof item !== 'object' || item === null) return [];
    const record = item as Record<string, unknown>;
    return [{
      question: typeof record.question === 'string' ? record.question : '',
      answer: typeof record.answer === 'string' ? record.answer : '',
    }];
  });
}

export function themeCssVars(tokens: ThemeTokens): Record<string, string> {
  const radius = RADIUS[tokens.radius];
  return {
    '--color-primary': tokens.primary,
    '--color-primary-dark': darkenHex(tokens.primary),
    '--color-secondary': tokens.secondary,
    '--font-sans': FONT_STACK[tokens.font],
    '--radius-md': radius,
    '--radius-lg': radius,
    '--radius-xl': radius,
    '--radius-2xl': radius,
    '--radius-3xl': radius,
  };
}

export function themeFontFamily(font: ThemeTokens['font']): string {
  return FONT_STACK[font];
}

export function ensureThemeFont(font: ThemeTokens['font']): void {
  if (typeof document === 'undefined') return;
  const id = `theme-font-${font}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = FONT_HREF[font];
  document.head.appendChild(link);
}

export function draftAsPublicTheme(document: ThemeDocument): PublicTheme {
  return {
    presetId: document.presetId,
    tokens: document.designTokens,
    sections: document.sectionsDraft,
    customCss: document.customCss,
    logoUrl: document.logoUrl,
    faviconUrl: document.faviconUrl,
    agency: {
      name: '',
      city: '',
      address: '',
      phone: '',
      whatsapp: '',
      ice: '',
      rc: '',
      patente: '',
      hours: [],
    },
  };
}

export function previewTargetOrigin(previewUrl: string): string {
  return new URL(previewUrl).origin;
}

export function isPlatformPreviewOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname.trim().toLowerCase().replace(/\.$/, '');
    return PLATFORM_HOSTS.includes(host) || host === 'localhost' || host === '127.0.0.1';
  } catch {
    return false;
  }
}
